import React, {
  ReactNode,
  useState,
  createContext,
  useContext,
  useEffect
} from 'react';
import axios from 'axios';

export interface Educator {
  id?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  email?: string;
  image?: string;
  state?: string;
  gradeLevel?: string;
  role?: string;
  school?: string;
  bookmarks?: string[];
  federationProvider?: string;
  organization?: string;
  reminder?: '0' | '1';
  partner?: string;
}

export type UserContextProps = {
  educator?: Educator;
  updateEducator: (value: Educator) => Promise<void>;
  updateBookmarkList: (id: string) => void;
};

export const UserContext = createContext<UserContextProps>({
  educator: {} as Educator,
  updateEducator: async () => void 0,
  updateBookmarkList: () => ({})
});

export const useUserContext = () => useContext(UserContext);

export interface UserProviderProps {
  children?: ReactNode | ReactNode[];
  user?: Educator;
}

export function UserProvider({ children, user }: UserProviderProps) {
  const [educator, setEducator] = useState(user);

  useEffect(() => {
    setEducator(user);
  }, [user]);

  const updateEducator = async (userAttrs: Educator) => {
    await axios.patch('/api/user/update-profile', userAttrs);
    setEducator(userAttrs);
  };

  const updateBookmarkList = async (lessonPlanId: string) => {
    const favorites = educator?.bookmarks || [];

    if (favorites?.includes(lessonPlanId)) {
      favorites.splice(favorites.indexOf(lessonPlanId), 1);
    } else {
      favorites.push(lessonPlanId);
    }

    await axios.patch('/api/user/update-profile', {
      bookmarks: favorites
    });

    setEducator((prev) => ({
      ...prev,
      bookmarks: favorites
    }));
  };

  return (
    <UserContext.Provider
      value={{
        educator,
        updateEducator,
        updateBookmarkList
      }}
    >
      {children}
    </UserContext.Provider>
  );
}
